import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../../services/aaiApi';
import type { AppAccess, AppAccessMode } from '../../../types/aai';
import { Action, Kicker, Modal, Toggle } from '../../aa';

type OffMode = Exclude<AppAccessMode, 'on'>;

/* The app being decided on: a row on the company's list, or a Composio search
   hit nobody has added yet. */
export interface AccessTarget {
  slug: string;
  label: string;
  logo?: string;
  access?: AppAccess;
}

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

const summaryOf = (access: AppAccess): string => {
  if (access.mode === 'owner') return 'Only the Owner';
  if (access.mode === 'off') return 'Off for everyone';
  return 'Everyone';
};

const OPTIONS: { mode: OffMode; title: string; desc: string }[] = [
  { mode: 'owner', title: 'Only allow the Owner', desc: 'You can still use it. Nobody else sees it or can connect it.' },
  { mode: 'off', title: 'Turn off for everyone', desc: 'Nobody can connect or use it, you included, until you turn it back on.' },
];

export const TurnOffAppDialog: React.FC<{
  target: AccessTarget;
  onClose: () => void;
  onSaved: () => void;
}> = ({ target, onClose, onSaved }) => {
  let initialMode: OffMode = 'owner';
  if (target.access?.mode === 'off') initialMode = 'off';
  const [mode, setMode] = useState<OffMode>(initialMode);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await aaiApi.setAppAccess(target.slug, mode, { label: target.label, logo: target.logo });
      toast.success(`${target.label}: ${OPTIONS.find((o) => o.mode === mode)?.title.toLowerCase()}.`);
      onSaved();
    } catch (err) {
      toast.error(detailOf(err, `Could not change who can use ${target.label}.`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context={<Kicker>App access</Kicker>}
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="quiet" onClick={onClose}>Cancel</Action>
          <Action disabled={busy} reason="Saving" onClick={save}>Turn off</Action>
        </div>
      )}
    >
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">Turn off {target.label}</h2>
      <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">
        It disappears for everyone else at your company, from their apps and from search. Connections they already made stop working.
      </p>
      <div className="mt-5 space-y-2">
        {OPTIONS.map((o) => {
          let frame = 'cursor-pointer border-line';
          if (mode === o.mode) frame = 'cursor-pointer border-ink';
          return (
            <label key={o.mode} className={`flex items-start gap-3 rounded-card border px-3.5 py-3 ${frame}`}>
              <input
                type="radio"
                name="app-access"
                className="mt-1"
                checked={mode === o.mode}
                onChange={() => setMode(o.mode)}
              />
              <span>
                <span className="block text-[14px] font-semibold text-ink">{o.title}</span>
                <span className="block text-[13px] leading-[20px] text-ink-muted">{o.desc}</span>
              </span>
            </label>
          );
        })}
      </div>
    </Modal>
  );
};

/* The Owner's per-app switch. On means everyone. Switching it off asks how:
   kept for the Owner, or off for everyone. */
export const AccessCell: React.FC<{ row: AccessTarget; onChanged: () => void }> = ({ row, onChanged }) => {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const access: AppAccess = row.access || { mode: 'on' };
  const on = access.mode === 'on';

  const turnOn = async () => {
    setBusy(true);
    try {
      await aaiApi.setAppAccess(row.slug, 'on');
      toast.success(`${row.label} is on for everyone.`);
      onChanged();
    } catch (err) {
      toast.error(detailOf(err, `Could not turn ${row.label} on.`));
    } finally {
      setBusy(false);
    }
  };

  const change = (next: boolean) => {
    if (next) {
      turnOn();
      return;
    }
    setAsking(true);
  };

  return (
    <span className="flex items-center gap-2.5">
      <Toggle checked={on} disabled={busy} label={`${row.label} on for everyone`} onChange={change} />
      {!on && (
        <button type="button" onClick={() => setAsking(true)} className="max-w-[180px] truncate text-left text-[12.5px] text-ink-muted underline-offset-2 hover:text-ink hover:underline">
          {summaryOf(access)}
        </button>
      )}
      {asking && (
        <TurnOffAppDialog
          target={row}
          onClose={() => setAsking(false)}
          onSaved={() => { setAsking(false); onChanged(); }}
        />
      )}
    </span>
  );
};
